import { NET, WS_PATH } from '@blorbs/common';
import type { BotDifficulty, ClientMessage, FireballState, GameEvent, InputMessage, PlayerState, ServerMessage, Snapshot, Vec } from '@blorbs/common';

const BUFFER_SIZE = 16;
const PING_INTERVAL_MS = 1000;
const RTT_WINDOW = 8;
/** how many snapshot inter-arrival gaps we look at when sizing the interpolation buffer */
const JITTER_WINDOW = 90;
const MIN_INTERP_MS = 40;
const MAX_INTERP_MS = 160;

interface Buffered {
  snap: Snapshot;
  /** local receive time */
  at: number;
}

export interface WorldView {
  phase: Snapshot['phase'];
  phaseEndsAt: number | null;
  islandRadius: number;
  players: PlayerState[];
  fireballs: FireballState[];
  winnerId: string | null;
  /** estimated current server time */
  serverNow: number;
}

/**
 * Plain WebSocket client. Keeps a short buffer of snapshots and renders
 * remote state `NET.interpDelayMs` behind the newest one so there is always
 * a pair to interpolate between.
 */
export class Net {
  myId = '';
  /** median round trip over the last few pings (0 until the first returns) */
  rtt = 0;
  /** spread between the best and worst recent ping */
  rttJitter = 0;
  /** diagnostics from the server: edge entry colo → room colo */
  route = '';
  /** current interpolation delay, adapted to snapshot arrival jitter */
  interpDelay = NET.interpDelayMs;
  onDisconnect: ((reason: string) => void) | null = null;

  private ws: WebSocket | null = null;
  private buffer: Buffered[] = [];
  private clockOffset = 0; // serverTime - localTime
  private eventHandlers: Array<(e: GameEvent) => void> = [];
  private snapshotHandlers: Array<(s: Snapshot) => void> = [];
  private pings = new Map<number, number>();
  private pingSeq = 0;
  private rttSamples: number[] = [];
  private gaps: number[] = [];
  private lastArrival = 0;

  constructor(private readonly basePath: string) {
    setInterval(() => {
      if (this.ws?.readyState !== WebSocket.OPEN) return;
      const n = ++this.pingSeq;
      this.pings.set(n, performance.now());
      if (this.pings.size > 5) this.pings.delete(n - 5);
      this.send({ t: 'ping', n });
    }, PING_INTERVAL_MS);
  }

  join(room: string, name: string): Promise<string> {
    const proto = location.protocol === 'https:' ? 'wss:' : 'ws:';
    const url = `${proto}//${location.host}${this.basePath}${WS_PATH}?room=${encodeURIComponent(room)}`;
    return new Promise((resolve, reject) => {
      const ws = new WebSocket(url);
      this.ws = ws;
      ws.onopen = () => this.send({ t: 'join', name });
      ws.onmessage = (ev) => {
        const msg = JSON.parse(ev.data as string) as ServerMessage;
        if (msg.t === 'joined') {
          this.myId = msg.id;
          this.clockOffset = msg.serverTime - Date.now();
          if (msg.entryColo || msg.roomColo) this.route = `${msg.entryColo || '?'}→${msg.roomColo || '?'}`;
          resolve(msg.id);
          return;
        }
        this.receive(msg);
      };
      ws.onerror = () => reject(new Error('Could not connect to the game server.'));
      ws.onclose = (ev) => {
        if (!this.myId) reject(new Error('Connection closed before joining.'));
        this.onDisconnect?.(ev.reason || 'Disconnected from the game server.');
      };
    });
  }

  onEvent(handler: (e: GameEvent) => void): void {
    this.eventHandlers.push(handler);
  }

  /** Raw snapshots as they arrive (prediction reconciles on these). */
  onSnapshot(handler: (s: Snapshot) => void): void {
    this.snapshotHandlers.push(handler);
  }

  newest(): Snapshot | null {
    return this.buffer[this.buffer.length - 1]?.snap ?? null;
  }

  sendInput(msg: InputMessage): void {
    this.send({ t: 'input', ...msg });
  }

  shoot(dir: Vec): void {
    this.send({ t: 'shoot', dir, lead: Math.round(this.rtt / 2) });
  }

  /** How far behind "server now" the rendered world is: one-way trip plus the interpolation buffer. */
  get renderLagMs(): number {
    return this.rtt / 2 + this.interpDelay;
  }

  shockwave(): void {
    this.send({ t: 'shockwave' });
  }

  setBots(count: number, difficulty?: BotDifficulty): void {
    this.send({ t: 'bots', count, difficulty });
  }

  get serverNow(): number {
    return Date.now() + this.clockOffset;
  }

  private send(msg: ClientMessage): void {
    if (this.ws?.readyState === WebSocket.OPEN) this.ws.send(JSON.stringify(msg));
  }

  private receive(msg: ServerMessage): void {
    switch (msg.t) {
      case 'snap': {
        const s = msg.s;
        const at = performance.now();
        this.clockOffset = s.t - Date.now();
        this.buffer.push({ snap: s, at });
        if (this.buffer.length > BUFFER_SIZE) this.buffer.shift();
        this.trackJitter(at);
        for (const h of this.snapshotHandlers) h(s);
        break;
      }
      case 'event':
        for (const h of this.eventHandlers) h(msg.e);
        break;
      case 'pong': {
        const t0 = this.pings.get(msg.n);
        if (t0 !== undefined) {
          this.pings.delete(msg.n);
          this.rttSamples.push(performance.now() - t0);
          if (this.rttSamples.length > RTT_WINDOW) this.rttSamples.shift();
          const sorted = [...this.rttSamples].sort((a, b) => a - b);
          this.rtt = Math.round(sorted[Math.floor(sorted.length / 2)]!);
          this.rttJitter = Math.round(sorted[sorted.length - 1]! - sorted[0]!);
        }
        break;
      }
    }
  }

  /**
   * Snapshots should arrive every tick; when they bunch up (proxy, wifi) the
   * renderer runs out of pairs to interpolate and remote orbs freeze. Size the
   * delay from the 90th-percentile arrival gap so that rarely happens, and ease
   * changes so the world doesn't visibly shift when the estimate moves.
   */
  private trackJitter(at: number): void {
    if (this.lastArrival) {
      this.gaps.push(at - this.lastArrival);
      if (this.gaps.length > JITTER_WINDOW) this.gaps.shift();
    }
    this.lastArrival = at;
    if (this.gaps.length < 10) return;
    const sorted = [...this.gaps].sort((a, b) => a - b);
    const p90 = sorted[Math.floor(sorted.length * 0.9)]!;
    const target = Math.min(MAX_INTERP_MS, Math.max(MIN_INTERP_MS, p90 + 12));
    this.interpDelay += (target - this.interpDelay) * 0.05;
  }

  /** Interpolated view of the world for rendering at local time `now` (performance.now()). */
  view(now: number): WorldView | null {
    const buf = this.buffer;
    if (buf.length === 0) return null;
    const target = now - this.interpDelay;
    const newest = buf[buf.length - 1]!;

    // Find the pair (a, b) with a.at <= target <= b.at.
    let a = newest;
    let b = newest;
    for (let i = buf.length - 1; i > 0; i--) {
      if (buf[i - 1]!.at <= target) {
        a = buf[i - 1]!;
        b = buf[i]!;
        break;
      }
    }
    const span = b.at - a.at;
    const t = span > 0 ? Math.min(1, Math.max(0, (target - a.at) / span)) : 1;

    const bPlayers = new Map(b.snap.players.map((p) => [p.id, p]));
    const players = a.snap.players.flatMap((pa) => {
      const pb = bPlayers.get(pa.id);
      if (!pb) return [];
      return [{ ...pb, x: lerp(pa.x, pb.x, t), y: lerp(pa.y, pb.y, t) }];
    });
    // Players only present in the newer snapshot (just joined) — show as-is.
    const seen = new Set(players.map((p) => p.id));
    for (const p of b.snap.players) if (!seen.has(p.id)) players.push(p);

    const bFire = new Map(b.snap.fireballs.map((f) => [f.id, f]));
    const fireballs = a.snap.fireballs.flatMap((fa) => {
      const fb = bFire.get(fa.id);
      if (!fb) return [];
      return [{ ...fb, x: lerp(fa.x, fb.x, t), y: lerp(fa.y, fb.y, t) }];
    });
    const seenF = new Set(fireballs.map((f) => f.id));
    for (const f of b.snap.fireballs) if (!seenF.has(f.id)) fireballs.push(f);

    return {
      phase: newest.snap.phase,
      phaseEndsAt: newest.snap.phaseEndsAt,
      islandRadius: lerp(a.snap.islandRadius, b.snap.islandRadius, t),
      players,
      fireballs,
      winnerId: newest.snap.winnerId,
      serverNow: this.serverNow,
    };
  }
}

const lerp = (a: number, b: number, t: number): number => a + (b - a) * t;
